import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Chip,
  Button,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Mic as MicIcon,
  Schedule as ScheduleIcon,
  Folder as FolderIcon,
  Upload as UploadIcon,
  Star as StarIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { useUnifiedTranslation } from '../../hooks/useUnifiedTranslation';
import { speakerApi } from '../../services/api/speaker.api';
import { DataTable } from '../ui/DataTable';
import { formatShortDate, getLocale } from '../../utils/date-utils';
import { PAGE_SIZE } from '../../constants/pagination';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`speaker-tabpanel-${index}`}
      aria-labelledby={`speaker-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export const SpeakerDashboard: React.FC = () => {
  const { t } = useUnifiedTranslation({ namespace: 'dashboard' });
  const ts = (key: string, fallback: string): string => { const v = t(key); return typeof v === 'string' ? v : fallback; };
  const [activeTab, setActiveTab] = useState(0);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedPresentationId, setSelectedPresentationId] = useState<string>('');

  // Загрузка выступлений
  const { data: presentationsData, isLoading: isLoadingPresentations, refetch: _refetchPresentations } = useQuery({
    queryKey: ['speaker', 'presentations'],
    queryFn: async () => {
      const response = await speakerApi.getPresentations({ limit: PAGE_SIZE.DEFAULT });
      return response.data;
    },
  });

  // Загрузка расписания
  const { data: scheduleData, isLoading: isLoadingSchedule } = useQuery({
    queryKey: ['speaker', 'schedule'],
    queryFn: async () => {
      const response = await speakerApi.getSchedule();
      return response.data;
    },
  });

  // Загрузка материалов
  const { data: materialsData, isLoading: isLoadingMaterials, refetch: refetchMaterials } = useQuery({
    queryKey: ['speaker', 'materials'],
    queryFn: async () => {
      const response = await speakerApi.getMaterials({ limit: PAGE_SIZE.DEFAULT });
      return response.data;
    },
  });

  // Загрузка профиля
  const { data: profileData, isLoading: isLoadingProfile } = useQuery({
    queryKey: ['speaker', 'profile'],
    queryFn: async () => {
      const response = await speakerApi.getProfile();
      return response.data;
    },
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleOpenUpload = (presentationId: string) => {
    setSelectedPresentationId(presentationId);
    setUploadDialogOpen(true);
  };

  const handleUploadMaterial = async () => {
    if (!selectedFile || !selectedPresentationId) return;

    try {
      await speakerApi.uploadMaterial(selectedFile, selectedPresentationId);
      alert('Материал успешно загружен!');
      setUploadDialogOpen(false);
      setSelectedFile(null);
      refetchMaterials();
    } catch (error) {
      console.error('Ошибка загрузки:', error);
      alert('Ошибка при загрузке материала');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Заголовок */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          {ts('speaker.title', 'Дашборд спикера')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {ts('speaker.subtitle', 'Управление выступлениями и материалами')}
        </Typography>
      </Box>

      {/* KPI карточки */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {ts('speaker.kpis.totalPresentations', 'Всего выступлений')}
                  </Typography>
                  <Typography variant="h4">
                    {isLoadingProfile ? <CircularProgress size={24} /> : profileData?.presentations || 0}
                  </Typography>
                </Box>
                <MicIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {ts('speaker.kpis.totalAttendees', 'Всего слушателей')}
                  </Typography>
                  <Typography variant="h4">
                    {isLoadingProfile ? <CircularProgress size={24} /> : profileData?.totalAttendees || 0}
                  </Typography>
                </Box>
                <PeopleIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {ts('speaker.kpis.rating', 'Рейтинг')}
                  </Typography>
                  <Typography variant="h4">
                    {isLoadingProfile ? <CircularProgress size={24} /> : profileData?.rating?.toFixed(1) || '0.0'}
                  </Typography>
                </Box>
                <StarIcon color="warning" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {ts('speaker.kpis.materials', 'Материалов')}
                  </Typography>
                  <Typography variant="h4">
                    {isLoadingMaterials ? <CircularProgress size={24} /> : materialsData?.total || 0}
                  </Typography>
                </Box>
                <FolderIcon color="info" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Вкладки */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label={ts('speaker.tabs.presentations', 'Выступления')} icon={<MicIcon />} iconPosition="start" />
            <Tab label={ts('speaker.tabs.schedule', 'Расписание')} icon={<ScheduleIcon />} iconPosition="start" />
            <Tab label={ts('speaker.tabs.materials', 'Материалы')} icon={<FolderIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        {/* Вкладка: Выступления */}
        <TabPanel value={activeTab} index={0}>
          {isLoadingPresentations ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : presentationsData?.presentations && presentationsData.presentations.length > 0 ? (
            <DataTable
              title={ts('speaker.myPresentations', 'Мои выступления')}
              columns={[
                {
                  id: 'title',
                  label: ts('speaker.columns.title', 'Название'),
                  sortable: true,
                },
                {
                  id: 'eventTitle',
                  label: ts('speaker.columns.event', 'Мероприятие'),
                  sortable: true,
                },
                {
                  id: 'date',
                  label: ts('speaker.columns.date', 'Дата'),
                  sortable: true,
                  render: (row: any) => formatShortDate(row.date),
                },
                {
                  id: 'duration',
                  label: ts('speaker.columns.duration', 'Длительность'),
                  render: (row: any) => `${row.duration} мин`,
                },
                {
                  id: 'status',
                  label: ts('speaker.columns.status', 'Статус'),
                  render: (row) => (
                    <Chip
                      label={row.status}
                      color={
                        row.status === 'completed' ? 'success' :
                        row.status === 'scheduled' ? 'info' :
                        'default'
                      }
                      size="small"
                    />
                  ),
                },
                {
                  id: 'attendees',
                  label: ts('speaker.columns.attendees', 'Слушателей'),
                },
                {
                  id: 'actions',
                  label: ts('speaker.columns.actions', 'Действия'),
                  render: (row: any) => (
                    <Button
                      size="small"
                      startIcon={<UploadIcon />}
                      onClick={() => handleOpenUpload(row.id)}
                    >
                      Загрузить материал
                    </Button>
                  ),
                },
              ]}
              data={presentationsData.presentations}
              rowKey="id"
              pagination
            />
          ) : (
            <Alert severity="info">
              {ts('speaker.noPresentations', 'Нет выступлений')}
            </Alert>
          )}
        </TabPanel>

        {/* Вкладка: Расписание */}
        <TabPanel value={activeTab} index={1}>
          {isLoadingSchedule ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : scheduleData?.schedule && scheduleData.schedule.length > 0 ? (
            <Box>
              {scheduleData.schedule.map((day) => (
                <Card key={day.date} sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {new Date(day.date).toLocaleDateString(getLocale(), { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </Typography>
                    <List>
                      {day.presentations.map((pres) => (
                        <ListItem key={pres.id}>
                          <ListItemText
                            primary={pres.title}
                            secondary={`${pres.time} • ${pres.duration} мин • ${pres.location}`}
                          />
                          <Chip
                            label={pres.status}
                            color={pres.status === 'scheduled' ? 'primary' : 'default'}
                            size="small"
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Alert severity="info">
              {ts('speaker.noSchedule', 'Расписание пусто')}
            </Alert>
          )}
        </TabPanel>

        {/* Вкладка: Материалы */}
        <TabPanel value={activeTab} index={2}>
          {isLoadingMaterials ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : materialsData?.materials && materialsData.materials.length > 0 ? (
            <DataTable
              title={ts('speaker.myMaterials', 'Мои материалы')}
              columns={[
                {
                  id: 'name',
                  label: ts('speaker.columns.name', 'Название'),
                  sortable: true,
                },
                {
                  id: 'type',
                  label: ts('speaker.columns.type', 'Тип'),
                },
                {
                  id: 'size',
                  label: ts('speaker.columns.size', 'Размер'),
                  render: (row: any) => `${(row.size / 1024 / 1024).toFixed(2)} MB`,
                },
                {
                  id: 'downloads',
                  label: ts('speaker.columns.downloads', 'Скачиваний'),
                },
                {
                  id: 'uploadedAt',
                  label: ts('speaker.columns.uploaded', 'Загружен'),
                  render: (row: any) => formatShortDate(row.uploadedAt),
                },
              ]}
              data={materialsData.materials}
              rowKey="id"
              pagination
            />
          ) : (
            <Alert severity="info">
              {ts('speaker.noMaterials', 'Нет материалов')}
            </Alert>
          )}
        </TabPanel>
      </Card>

      {/* Диалог загрузки материала */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Загрузить материал</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <input
              type="file"
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              accept=".pdf,.ppt,.pptx,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Максимальный размер: 50MB. Поддерживаемые форматы: PDF, PPT, DOC, XLS, изображения
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>
            Отмена
          </Button>
          <Button onClick={handleUploadMaterial} variant="contained" disabled={!selectedFile}>
            Загрузить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SpeakerDashboard;

